import { MutableState } from 'src/replicant/State';
import {
  canClaimRaceRewards,
  canParticipateOnCurrentRace,
  getActiveRace,
  getRaceOpponents,
  getRaceRewards,
  getRaceStartTime,
} from './races.getters';
import { grantMatch2Rewards } from '../match2.modifiers';
import { duration } from 'src/replicant/utils/duration';
import { CurrencyReplicantAPI } from 'src/replicant/inventory/inventory.utils';
import { BotsPrivateApi } from './races.bots';

/**
 * Joins the current active race.
 * @param state
 * @param now
 */
export function joinActiveRace(
  state: MutableState,
  api: CurrencyReplicantAPI,
): void {
  const now = api.date.now();
  if (!canParticipateOnCurrentRace(state, now)) {
    throw new Error('User cannot join active race.');
  }

  const { id, config } = getActiveRace(now)!;

  if (!state.match2.races[id]) {
    state.match2.races[id] = [];
  }

  state.match2.races[id].push({
    id,
    score: 0,
    createdAt: now,
    modifiedAt: now,
  });

  const index = state.match2.races[id].length - 1;
  state.match2.races[id][index].bots = [];
  state.match2.races[id][index].bots!.push(
    ...getRaceOpponents(state, id, index, now).map((bot) => ({
      score: bot.score,
      modifiedAt: now,
      createdAt: now,
    })),
  );

  // Add free energy for the duration of the event. Only for timed events.
  if (config.type === 'time') {
    // Because this is timed energy, this will not send currency grant event, but feature and subfeature are required.
    grantMatch2Rewards(
      state,
      [
        {
          type: 'energy',
          duration: duration({ hours: config.raceDurationHours }),
        },
      ],
      {
        feature: 'match_race',
        $subFeature: `temporary_energy`,
      },
      api,
    );
  }
}

/**
 * Update score.
 * @param state
 * @param now
 * @param id
 * @param score
 */
export function updateRaceScore(
  state: MutableState,
  now: number,
  id: string,
  raceIndex: number,
  score: number,
): void {
  if (!state.match2.races[id]?.[raceIndex]) {
    throw new Error('Cannot update score, need to join a race first');
  }

  // Also update bot scores.
  BotsPrivateApi.tickRaceBots('onPlayerScored', state, now);

  state.match2.races[id][raceIndex].score += score;
  state.match2.races[id][raceIndex].modifiedAt = now;

  // Cap score at target.
  const { config } = getActiveRace(getRaceStartTime(id)) ?? {};
  if (config?.type === 'target') {
    state.match2.races[id][raceIndex].score = Math.min(
      state.match2.races[id][raceIndex].score,
      config.target,
    );
  }
}

/**
 * Clear races older than 14 days.
 * @param state
 * @param now
 */
export function clearOldRaces(state: MutableState, now: number): void {
  const racesToRemove = Object.keys(state.match2.races).filter(
    (x) =>
      now >=
      state.match2.races[x][state.match2.races[x].length - 1].createdAt +
        duration({ days: 14 }),
  );

  for (const raceId of racesToRemove) {
    delete state.match2.races[raceId];
  }
}

/**
 * Claim all race rewards.
 * @param state
 * @param raceId
 * @param raceIndex
 * @param now
 */
export function claimRaceRewards(
  state: MutableState,
  raceId: string,
  raceIndex: number,
  api: CurrencyReplicantAPI,
): void {
  const now = api.date.now();

  // Player cannot claim rewards.
  if (!canClaimRaceRewards(state, raceId, raceIndex, now)) return;

  const race = state.match2.races[raceId]![raceIndex];

  // Mark as claimed.
  race.claimedRewardAt = now;

  // Grant rewards.
  const rewards = getRaceRewards(state, raceId, raceIndex, now);
  const { config } = getActiveRace(race.createdAt)!;
  grantMatch2Rewards(
    state,
    rewards,
    {
      feature: 'match_race',
      $subFeature: `${config.type}_${config.goal}`,
    },
    api,
  );
}
